import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';

import { cn } from '@/lib/cn';
import { useAppFeatures } from '@/lib/features';
import { texts } from '@/lib/texts';

export type Tab = 'home' | 'squad' | 'season' | 'profile' | 'my' | 'pool';

interface TabDef {
  id: Tab;
  path: string;
  label: string;
  icon: ReactNode;
}

const LEGACY_TABS: ReadonlyArray<{ id: Tab; path: string; label: string }> = [
  { id: 'home', path: '/home', label: texts.nav.home },
  { id: 'squad', path: '/squad', label: texts.nav.squad },
  { id: 'season', path: '/season', label: texts.nav.season },
  { id: 'profile', path: '/profile', label: texts.nav.profile },
];

/** Иконки панели (design.md 4.4): сетка 24, линия 1,6, один-два тонких штриха «гравюры». */
function Icon({ children }: { children: ReactNode }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const ICONS: Record<Tab, ReactNode> = {
  home: (
    <Icon>
      <path d="M3.5 11 12 4l8.5 7" />
      <path d="M5.5 9.5V20h13V9.5" />
      <path d="M10 20v-5.5h4V20" />
    </Icon>
  ),
  my: (
    <Icon>
      <path d="M5 4.5h11.5A2.5 2.5 0 0 1 19 7v13H7.5A2.5 2.5 0 0 1 5 17.5v-13Z" />
      <path d="M5 17.5A2.5 2.5 0 0 1 7.5 15H19" />
      <path d="M9 8h6M9 11h4" strokeWidth="1.1" />
      <path d="M15.5 4.5v5l1.2-1 1.2 1" strokeWidth="1.1" />
    </Icon>
  ),
  squad: (
    <Icon>
      <path d="M12 3 5 6v6c0 4 3 6.6 7 9 4-2.4 7-5 7-9V6l-7-3Z" />
      <path d="M9 9.5v4.5M11.2 8.6v7.4M13.4 8.6v7.8M15.6 9.5v4.9" strokeWidth=".9" opacity=".75" />
    </Icon>
  ),
  season: (
    <Icon>
      <rect x="4" y="5.5" width="16" height="14.5" rx="1.5" />
      <path d="M4 9.5h16M8.5 3.5v4M15.5 3.5v4" />
      <path
        d="M8 13h.01M12 13h.01M16 13h.01M8 16.5h.01M12 16.5h.01M16 16.5h.01"
        strokeWidth="2.2"
      />
    </Icon>
  ),
  profile: (
    <Icon>
      <ellipse cx="12" cy="12" rx="7.5" ry="9" />
      <circle cx="12" cy="10" r="2.6" />
      <path d="M7.8 17.6c1-1.9 2.4-2.8 4.2-2.8s3.2.9 4.2 2.8" />
    </Icon>
  ),
  pool: (
    <Icon>
      <circle cx="9" cy="9" r="3" />
      <path d="M3.5 19c.8-3 2.9-4.5 5.5-4.5s4.7 1.5 5.5 4.5" />
      <path d="M16 7.5v5M13.5 10h5" />
    </Icon>
  ),
};

const tab = (id: Tab, path: string, label: string): TabDef => ({
  id,
  path,
  label,
  icon: ICONS[id],
});

/** Участник отряда с «Моими сборами»: пять вкладок. */
const SQUAD_TABS: readonly TabDef[] = [
  tab('home', '/home', texts.nav.home),
  tab('my', '/my', texts.nav.my),
  tab('squad', '/squad', texts.nav.squad),
  tab('season', '/season', texts.nav.season),
  tab('profile', '/profile', texts.nav.profile),
];

/**
 * Человек в пуле (Stage 17: панель появляется впервые). «Дом», «Отряд» и «Сезон» ему пусты —
 * ведут на «Ты пока не в отряде», поэтому их нет: набор, записи, профиль.
 */
const POOL_TABS: readonly TabDef[] = [
  tab('pool', '/pool', texts.nav.pool),
  tab('my', '/my', texts.nav.my),
  tab('profile', '/profile', texts.nav.profile),
];

/**
 * Нижняя навигация (BLUEPRINT 3.8). Без `feature.myGatherings` — прежние четыре вкладки участника
 * отряда, как в rc.30. С флагом — вкладка «Мои сборы» и иконки: пять подписей в строку на 320 px
 * без иконок не помещались бы (design.md 4.4).
 */
export function TabBar({ active }: { active: Tab }) {
  const navigate = useNavigate();
  const { myGatherings, state } = useAppFeatures();

  if (!myGatherings) {
    return (
      <nav
        className="bg-surface rounded-card grid grid-cols-4 gap-1 p-1"
        aria-label={texts.app.title}
      >
        {LEGACY_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            aria-current={t.id === active ? 'page' : undefined}
            onClick={() => {
              if (t.id !== active) void navigate(t.path);
            }}
            className={cn(
              'rounded-control pressable h-11 text-sm font-semibold',
              t.id === active ? 'bg-surface-2 text-accent' : 'text-muted active:bg-surface-2',
            )}
          >
            {t.label}
          </button>
        ))}
      </nav>
    );
  }

  const tabs = state === 'inSquad' ? SQUAD_TABS : POOL_TABS;
  return (
    <nav
      className={cn(
        'bg-surface rounded-card grid gap-0.5 p-1',
        tabs.length === 5 ? 'grid-cols-5' : 'grid-cols-3',
      )}
      aria-label={texts.app.title}
    >
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          aria-current={t.id === active ? 'page' : undefined}
          onClick={() => {
            if (t.id !== active) void navigate(t.path);
          }}
          className={cn(
            // Без боковых полей и на 10 px при узком экране: «Мои сборы» (54 px) не влезали в ячейку
            // 50 px на 320 и обрезались (ревью 2026-09-24)
            'rounded-control pressable flex h-[54px] min-w-0 flex-col items-center justify-center gap-0.5 px-0',
            'text-[10.5px] leading-[1.1] font-semibold max-[360px]:text-[10px]',
            t.id === active ? 'bg-surface-2 text-accent' : 'text-muted active:bg-surface-2',
          )}
        >
          {t.icon}
          {/* Раунд персон 1 (M10/C10): «Мои сборы» в одну строку и на 320 px — подписи без
              переноса, чуть плотнее; ячейка шириной в пятую часть панели её вмещает */}
          <span className="max-w-full text-center whitespace-nowrap tracking-[-0.02em]">
            {t.label}
          </span>
        </button>
      ))}
    </nav>
  );
}
